import { Input, Label } from '@slal/ui/forms';
import { Col, Row } from '@slal/ui/layout';
import { useController } from '@slal/ui/react-hook-form/core';
import { TextFieldProps } from '@slal/ui/react-hook-form/fields';
import { padStart } from 'lodash';
import { useCallback } from 'react';
import { normalizePaddedNumbersRange } from '~/utils';
import { StyledDateFieldSet, StyledFieldSet, StyledInput } from './styles';

export const DateField = ({ name, ...otherProps }: TextFieldProps) => {
    const {
        field: { onChange, ref, ...field },
    } = useController({ name, defaultValue: '' });

    const dayName = `${name}-day`;
    const monthName = `${name}-month`;
    const yearName = `${name}-year`;
    const parts = field.value ? field.value.split('-') : ['', '', ''];

    const updateDay: React.ChangeEventHandler<HTMLInputElement> = useCallback(
        event => {
            const value = event.target?.value || '';
            const setValueAs = normalizePaddedNumbersRange(0, 31);
            const nextValue = setValueAs(value, parts[2]);
            onChange(`${parts[0] || ''}-${parts[1] || ''}-${nextValue}`);
        },
        [parts, onChange]
    );

    const updateMonth: React.ChangeEventHandler<HTMLInputElement> = useCallback(
        event => {
            const value = event.target?.value || '';
            const setValueAs = normalizePaddedNumbersRange(0, 12);
            const nextValue = setValueAs(value, parts[1]);
            onChange(`${parts[0] || ''}-${nextValue}-${parts[2] || ''}`);
        },
        [parts, onChange]
    );

    const updateYear: React.ChangeEventHandler<HTMLInputElement> = useCallback(
        event => {
            const value = event.target?.value || '';
            const setValueAs = normalizePaddedNumbersRange(0, 2050);
            const nextValue = setValueAs(value, parts[0]);
            onChange(`${nextValue}-${parts[1] || ''}-${parts[2] || ''}`);
        },
        [parts, onChange]
    );

    const updateValue = useCallback(() => {
        const day = parts[2] && parts[2] !== '0' ? padStart(parts[2], 2, '0') : parts[2] || '';
        const month = parts[1] && parts[1] !== '0' ? padStart(parts[1], 2, '0') : parts[1] || '';
        const year = parts[0] && parts[0] !== '0' ? padStart(parts[0], 4, '0') : parts[0] || '';
        onChange(`${year}-${month}-${day}`);
    }, [parts, onChange, ref]);

    return (
        <StyledDateFieldSet>
            <Input type="hidden" id={name} {...field} {...otherProps} />

            <Row gutters={false}>
                <Col col="auto">
                    <Label htmlFor={dayName}>Day</Label>
                    <StyledInput
                        value={parts[2] || ''}
                        id={dayName}
                        name={dayName}
                        maxLength={2}
                        onChange={updateDay}
                        onBlur={updateValue}
                        placeholder="DD"
                        size={2}
                    />
                </Col>

                <Col col="auto">
                    <Label htmlFor={monthName}>Month</Label>
                    <StyledInput
                        value={parts[1] || ''}
                        id={monthName}
                        name={monthName}
                        maxLength={2}
                        onChange={updateMonth}
                        onBlur={updateValue}
                        placeholder="MM"
                        size={2}
                    />
                </Col>

                <Col col="auto">
                    <Label htmlFor={yearName}>Year</Label>
                    <StyledInput
                        value={parts[0] || ''}
                        id={yearName}
                        name={yearName}
                        maxLength={4}
                        onChange={updateYear}
                        onBlur={updateValue}
                        placeholder="YYYY"
                        size={4}
                    />
                </Col>
            </Row>
        </StyledDateFieldSet>
    );
};

export const SortCodeField = ({ name, ...otherProps }: TextFieldProps) => {
    const {
        field: { onChange, ref, ...field },
    } = useController({ name, defaultValue: '' });

    const segment1Name = `${name}-segment-1`;
    const segment2Name = `${name}-segment-2`;
    const segment3Name = `${name}-segment-3`;
    const parts = field.value ? field.value.split('-') : ['', '', ''];

    const updateSegment1: React.ChangeEventHandler<HTMLInputElement> = useCallback(
        event => {
            const value = event.target?.value || '';
            const setValueAs = normalizePaddedNumbersRange(0, 99);
            const nextValue = setValueAs(value, parts[0]);
            onChange(`${nextValue}-${parts[1] || ''}-${parts[2] || ''}`);
        },
        [parts, onChange]
    );

    const updateSegment2: React.ChangeEventHandler<HTMLInputElement> = useCallback(
        event => {
            const value = event.target?.value || '';
            const setValueAs = normalizePaddedNumbersRange(0, 99);
            const nextValue = setValueAs(value, parts[1]);
            onChange(`${parts[0] || ''}-${nextValue}-${parts[2] || ''}`);
        },
        [parts, onChange]
    );

    const updateSegment3: React.ChangeEventHandler<HTMLInputElement> = useCallback(
        event => {
            const value = event.target?.value || '';
            const setValueAs = normalizePaddedNumbersRange(0, 99);
            const nextValue = setValueAs(value, parts[2]);
            onChange(`${parts[0] || ''}-${parts[1] || ''}-${nextValue}`);
        },
        [parts, onChange]
    );

    const updateValue = useCallback(() => {
        const segment1 = parts[0] && parts[0] !== '0' ? padStart(parts[0], 2, '0') : parts[0] || '';
        const segment2 = parts[1] && parts[1] !== '0' ? padStart(parts[1], 2, '0') : parts[1] || '';
        const segment3 = parts[2] && parts[2] !== '0' ? padStart(parts[2], 2, '0') : parts[2] || '';
        onChange(`${segment1}-${segment2}-${segment3}`);
    }, [parts, onChange, ref]);

    return (
        <StyledFieldSet>
            <Input type="hidden" id={name} {...field} {...otherProps} data-testid="sort-code-value" />
            <Row gutters={false}>
                <Col col="auto">
                    <StyledInput
                        aria-label="Sort code segment 1"
                        value={parts[0] || ''}
                        id={segment1Name}
                        name={segment1Name}
                        maxLength={2}
                        onChange={updateSegment1}
                        onBlur={updateValue}
                        size={2}
                    />
                </Col>
                <Col col="auto">
                    <StyledInput
                        aria-label="Sort code segment 2"
                        value={parts[1] || ''}
                        id={segment2Name}
                        name={segment2Name}
                        maxLength={2}
                        onChange={updateSegment2}
                        onBlur={updateValue}
                        size={2}
                    />
                </Col>
                <Col col="auto">
                    <StyledInput
                        aria-label="Sort code segment 3"
                        value={parts[2] || ''}
                        id={segment3Name}
                        name={segment3Name}
                        maxLength={2}
                        onChange={updateSegment3}
                        onBlur={updateValue}
                        size={2}
                    />
                </Col>
            </Row>
        </StyledFieldSet>
    );
};
